import { ArrowBack, ArrowForward } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getVpcSubnetsByAccountApi } from "../../../api/stateFile/stateFileApi";
import InfoDialog from "../../../components/modal/InfoDialog";
import { setGlobalLoader } from "../../../redux/actions/commonAction";
import { setNewProvision } from "../../../redux/actions/provisionAction";
import { getRegionsByAccount } from "../../../redux/actions/stateFileAction";
import Types from "../../../redux/types";
import CustomSelect from "../components/customSelect";
import {
  hasSingleSubnet,
  selectField,
  textFields,
  vpcEnabledServices,
  vpcFormFields,
} from "../utils";
import { useStyles } from "./styles";
import { stateModel } from "./utils";

const ProvisionDetails = ({
  editMode = false,
  service = null,
  defaultState = null,
  onSubmit,
  show,
  onBack,
  userProvision = [],
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { allCredentials, allServices, allRegions } = useSelector((state) => ({
    allCredentials: state.credential.allCredentials,
    allServices: state.provision?.allServices,
    allRegions: state.stateFile?.allRegions,
  }));
  const classes = useStyles();
  const [state, setState] = useState(stateModel);
  const [vpcState, setVpcState] = useState({
    vpc: null,
    subnets: [],
  });
  const [vpcOptions, setVpcOptions] = useState([]);
  const [vpcFields, setVpcFields] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [fetchingRegion, setFetchingRegion] = useState(false);
  const [fetchingVpc, setFetchingVpc] = useState(false);
  const [provisionExists, setProvisionExists] = useState([]);
  const [nameError, setNameError] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (service) {
      setState((prevState) => ({
        ...prevState,
        service,
      }));

      if (state.accountNumber && state.region) {
        const account = state?.accountNumber?.credName,
          region = state?.region?.region;
        fetchVpcDetails(service, account, region);
      } else {
        setVpcFields([]);
      }
    }

    //eslint-disable-next-line
  }, [service, state?.accountNumber?.credName, state?.region?.region]);

  useEffect(() => {
    if (userProvision.length > 0) {
      if (state?.accountNumber?.accountNumber && state?.region?.region) {
        const exists = userProvision.filter(
          (prov) =>
            prov.status.includes("IN_PROGRESS") &&
            prov.accountNumber === state?.accountNumber?.accountNumber &&
            prov?.variables?.region === state?.region?.region
        );
        setShowError(exists.length > 0);
        setProvisionExists(exists);
      }
      if (state.name) {
        const nameExists = userProvision.find(
          (prov) =>
            prov.accountNumber === state?.accountNumber?.accountNumber &&
            prov?.variables?.region === state?.region?.region &&
            prov?.name === state.name
        );
        setNameError(
          Boolean(nameExists) ? "This Provision name is already existing!" : ""
        );
      }
    }
  }, [state?.accountNumber, state?.region, state.name, userProvision]);

  useEffect(() => {
    setState(stateModel);
    if (defaultState) {
      dispatch(setGlobalLoader(true));
      let newState = {
        accountNumber: defaultState?.accountNumber,
        region: defaultState?.variables?.region,
        service: defaultState?.service,
        name: defaultState?.name,
        description: defaultState?.description,
      };
      setState(newState);
      fetchRegion(defaultState?.accountName);
      fetchVpcDetails(
        defaultState?.service,
        defaultState?.accountName,
        defaultState?.variables?.region
      );
      dispatch(setGlobalLoader(false));
    }
    //eslint-disable-next-line
  }, [defaultState]);

  useEffect(() => {
    if (defaultState && vpcOptions?.length > 0) {
      if (vpcEnabledServices.includes(defaultState?.service)) {
        const vpc_id = defaultState?.tfVars.find(
          (obj) => obj.name === "vpc_id"
        )?.default;
        const subnet_ids = defaultState?.tfVars.find((obj) =>
          ["subnet_ids", "subnet_id"].includes(obj.name)
        )?.default;
        const selectedVpc = vpcOptions.find((obj) => obj.id === vpc_id);
        let subnets = [];
        for (const obj of selectedVpc?.subnets) {
          if (subnet_ids?.includes(obj.id)) subnets.push(obj);
        }
        setVpcState({
          vpc: selectedVpc,
          subnets: hasSingleSubnet(defaultState?.service)
            ? subnets[0]
            : subnets,
        });
      }
    }
    //eslint-disable-next-line
  }, [defaultState, vpcOptions]);

  useEffect(() => {
    const isValidForm = [...selectField, ...textFields].every(
      (field) => state[field.name]
    );
    let hasVpc = true;
    if (vpcFields?.length > 0) {
      if (vpcFields?.length === 2) {
        hasVpc =
          vpcState.vpc &&
          (vpcState?.subnets?.length >= 2 || vpcOptions?.length);
      } else {
        hasVpc = vpcState.vpc;
      }
    }
    setIsValid(
      isValidForm &&
        hasVpc &&
        !Boolean(nameError) &&
        provisionExists.length === 0
    );
    //eslint-disable-next-line
  }, [
    setIsValid,
    state,
    selectField,
    textFields,
    vpcFields,
    vpcState,
    nameError,
    provisionExists,
  ]);

  const fetchVpcSubnets = async (account, region) => {
    let response = await getVpcSubnetsByAccountApi(account, region);
    setVpcOptions(response);
    setFetchingVpc(false);
  };

  const updateLocalState = (key, value = "") => {
    let newKeys = {};
    if (key === "accountNumber") {
      newKeys["region"] = "";
      newKeys["service"] = defaultState?.service || "";
      setVpcFields([]);
      dispatch({
        type: Types.REGIONS_BY_ACCOUNT,
        payload: [],
      });
    }
    if (key === "region") {
      newKeys["service"] = defaultState?.service || "";
      if (!defaultState?.service) {
        // clear the vpc fields by changing region in create mode only
        setVpcFields([]);
      }
    }
    if (key === "service") {
      setVpcOptions([]);
      setVpcState({
        vpc: null,
        subnets: [],
      });
    }
    setState((prevState) => ({
      ...prevState,
      ...newKeys,
      [key]: value,
    }));
  };

  const fetchRegion = (credName) => {
    setFetchingRegion(true);
    dispatch(getRegionsByAccount(credName, () => setFetchingRegion(false)));
  };

  const fetchVpcDetails = (service, account, region) => {
    if (vpcEnabledServices.includes(service)) {
      setFetchingVpc(true);
      setVpcState({
        vpc: null,
        subnets: hasSingleSubnet(service) ? null : [],
      });
      fetchVpcSubnets(account, region);
      setVpcFields(service === "sg" ? ["vpc"] : ["vpc", "subnets"]);
    }
  };

  const handleChange = (key, value) => {
    updateLocalState(key, value);
    if (value) {
      if (key === "accountNumber") {
        fetchRegion(value?.credName);
      }
      if (key === "service") {
        if (state.accountNumber && state.region) {
          const account = state?.accountNumber?.credName,
            region = state?.region?.region;
          fetchVpcDetails(value, account, region);
        } else {
          setVpcFields([]);
        }
      }
    }
  };

  const handleVpcSubnets = (key, value) => {
    let newState = {};
    if (key === "vpc") {
      newState["subnets"] = hasSingleSubnet(state.service) ? null : [];
    }
    setVpcState((prevState) => ({
      ...prevState,
      ...newState,
      [key]: value,
    }));
  };

  const handleSubmit = async (newProvision) => {
    dispatch(setGlobalLoader(true));
    dispatch(setNewProvision(newProvision));
    onSubmit();
  };

  return (
    <Box sx={{ display: show ? "block" : "none" }}>
      <InfoDialog
        open={showError}
        type="warning"
        title="Provisions In-Progress!"
        content={`${provisionExists?.length} provision(s) are in progress in the selected account & region, 
          still you can create a new provision by complete/terminate the provisions in progress or create in different account/region`}
        onClose={() => setShowError(false)}
      />
      <Typography variant="h6" gutterBottom={true}>
        Provision Details
      </Typography>
      <Grid container spacing={2}>
        <Grid item md={4} xs={12}>
          <CustomSelect
            value={state.accountNumber}
            onChange={handleChange}
            options={allCredentials}
            data={selectField[0]}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <CustomSelect
            value={state.region}
            onChange={handleChange}
            options={allRegions}
            disabled={fetchingRegion || !state.accountNumber}
            data={selectField[1]}
            endAdornment={
              fetchingRegion ? (
                <CircularProgress color="primary" size={20} />
              ) : null
            }
          />
        </Grid>
        <Grid item md={5} xs={12}>
          <CustomSelect
            // disabled={defaultState || !state?.region}
            disabled={true}
            value={state?.service}
            onChange={handleChange}
            options={allServices}
            data={selectField[2]}
          />
        </Grid>
        {vpcFields?.length > 0 &&
          vpcFormFields.map((data) => {
            if (vpcFields.includes(data.name)) {
              let subnetOptions = vpcState?.vpc?.subnets || [];
              const isSubnetField = data.name === "subnets";
              return (
                <Grid item xs={12} md={isSubnetField ? 7 : 5}>
                  <CustomSelect
                    multiple={isSubnetField && !hasSingleSubnet(state.service)}
                    endAdornment={
                      fetchingVpc ? (
                        <CircularProgress color="primary" size={20} />
                      ) : null
                    }
                    key={data.name}
                    value={vpcState[data.name]}
                    onChange={handleVpcSubnets}
                    options={data.name === "vpc" ? vpcOptions : subnetOptions}
                    disabled={
                      fetchingVpc || isSubnetField ? !vpcState?.vpc : false
                    }
                    data={data}
                    getLabel={(val) => val?.name || val?.id}
                  />
                </Grid>
              );
            }
            return null;
          })}
        {textFields.map((data, index) => {
          const disabled = editMode && data.name === "name";
          return (
            <Grid item xs={12} md={index === 0 ? 4 : 8}>
              <TextField
                key={data.name}
                {...data}
                disabled={disabled}
                margin="normal"
                error={data.name === "name" ? Boolean(nameError) : false}
                helperText={data.name === "name" ? nameError : ""}
                fullWidth
                value={state[data.name] || ""}
                onChange={(e) => handleChange(data.name, e.target.value)}
              />
            </Grid>
          );
        })}
        <Box className={classes.footer}>
          <Button
            startIcon={<ArrowBack />}
            variant="outlined"
            onClick={() => (defaultState ? history.goBack() : onBack())}
          >
            Back
          </Button>
          <Button
            endIcon={<ArrowForward />}
            disabled={!isValid}
            variant="contained"
            onClick={() => handleSubmit({ ...state, ...vpcState })}
          >
            Next
          </Button>
        </Box>
      </Grid>
    </Box>
  );
};

export default ProvisionDetails;
